/// Berthing + anchoring techniques — beyond the single-anchor lunch
/// stop. Two domains:
///
///   1. Mooring techniques — alongside, fore-and-aft moorings,
///      springs, lazy-lines, Mediterranean stern-to. RYA Day Skipper
///      practical + Coastal Skipper material.
///   2. Anchoring extras — Bahamian moor, fore-and-aft anchor moor,
///      two-anchor setup, anchor watch. Coastal Skipper + Yachtmaster
///      material.

// ───────────────────────────────────────────────────────────────────
// 1. Mooring techniques
// ───────────────────────────────────────────────────────────────────

export type MooringTechniqueId =
  | "alongside"
  | "alongside-springs"
  | "fore-and-aft"
  | "lazy-lines"
  | "med-stern-to"
  | "raft-up"
  | "trot-mooring";

export interface MooringTechnique {
  id: MooringTechniqueId;
  name: string;
  /// One-line gist.
  summary: string;
  /// Common scenarios where you'd use this.
  whenToUse: string;
  /// Number + names of lines required at the boat end.
  linesRequired: readonly string[];
  /// Step-by-step procedure on arrival.
  arrivalSteps: readonly string[];
  /// Common mistakes + safety notes.
  pitfalls: readonly string[];
}

export const MOORING_TECHNIQUES: readonly MooringTechnique[] = Object.freeze([
  {
    id: "alongside",
    name: "Alongside (basic — bow + stern lines only)",
    summary:
      "Tie up with one line from the bow forward to a cleat ahead of the boat, and one from the stern aft to a cleat behind. The simplest moor — adequate when there is no current, no surge and you're staying briefly.",
    whenToUse:
      "Short stops on a finger pontoon in still water; fuel berth; lunch stop. NEVER overnight in tidal water or where the wind may shift.",
    linesRequired: ["Bow line (fwd, port or stbd)", "Stern line (aft, port or stbd)"],
    arrivalSteps: [
      "Approach into the wind / tide whichever is stronger — gives the bow + stern the best chance of stopping correctly.",
      "Brief crew BEFORE the approach: who steps off, where, with which line, on which cleat.",
      "Come alongside at minimum speed, parallel to the pontoon, fenders rigged + tied to stanchions or guard wires at the level of the rubbing strake.",
      "Crew steps off (NEVER jumps) with the midships line in hand first — that's the line that stops the boat.",
      "Make the midships line off on a pontoon cleat to stop the boat moving.",
      "Pass the bow + stern lines, tied securely on the boat's cleats first then to the pontoon.",
      "Adjust for free movement up + down with the tide if tidal.",
    ],
    pitfalls: [
      "Lines tied to the pontoon FIRST then thrown to the boat — when wind catches the boat, the pontoon cleat takes a snatch load + can rip out of soft wooden pontoons.",
      "Stepping off too early or too late — practise alongside in benign conditions before relying on a clean step-off in a crosswind.",
      "No midships line — bow + stern alone may pivot the boat broadside.",
      "Fenders not at the right height for the pontoon — strake instead of fender takes the impact.",
    ],
  },
  {
    id: "alongside-springs",
    name: "Alongside with springs",
    summary:
      "Add a fore spring (from a midships cleat, leading AFT) and a back spring (from a midships cleat, leading FORWARD) to stop the boat surging fore-and-aft along the pontoon.",
    whenToUse:
      "Overnight moorings, any wind-against-tide situation, anywhere with surge or wash, any harbour wall with no give. Standard for any non-trivial alongside moor.",
    linesRequired: [
      "Bow line (fwd, leads forward)",
      "Stern line (aft, leads aft)",
      "Fore spring — midships to a cleat AFT of the boat (stops the boat going forward)",
      "Back spring — midships to a cleat FORWARD of the boat (stops the boat going back)",
    ],
    arrivalSteps: [
      "Approach as for basic alongside, but plan to put springs on FIRST.",
      "Step off with the midships line as before; make it off on a pontoon cleat.",
      "Quickly tie the fore spring + back spring to the boat's midship cleat and lead them in opposite directions — they prevent the boat surging.",
      "Add the bow + stern lines to keep the boat off the pontoon when wind blows.",
      "Adjust all four lines for the expected tide range — slack enough to rise but tight enough not to surge.",
      "Whip excess line back to the boat to keep the pontoon tidy.",
    ],
    pitfalls: [
      "Confusing fore vs back spring — easy to remember: fore spring stops the boat going FORWARDS (leads aft, like a brake); back spring stops it going BACKWARDS (leads forward).",
      "Springs too tight — they prevent the boat rising on the tide and can take huge loads at high water. Allow some slack.",
      "Crossing the bow + stern lines over the springs — chafes both lines + makes adjustment fiddly. Lead each line cleanly to its own pontoon cleat.",
    ],
  },
  {
    id: "fore-and-aft",
    name: "Fore-and-aft moorings (between two buoys)",
    summary:
      "Boat moored between TWO buoys — one off the bow, one off the stern. Common in tidal rivers + crowded harbours where swinging-moor space is unavailable.",
    whenToUse:
      "Rivers + creeks where current rules out a swinging mooring; busy moorings rolled in trots; anywhere space-efficient mooring matters.",
    linesRequired: [
      "Bow line to the fwd buoy",
      "Stern line to the aft buoy",
      "Optional spring lines to additional buoys if installed",
    ],
    arrivalSteps: [
      "Identify the buoys + check which is bow + which is stern (consult the harbour master — buoys often have buoy numbers).",
      "Approach with the tide on your bow + the fwd buoy ahead.",
      "Pick up the fwd buoy with a boat hook + secure the bow line through the eye.",
      "Drop back to the aft buoy under engine in reverse OR let the tide carry you back.",
      "Pick up the aft buoy + secure the stern line.",
      "Adjust lines for tide range — needs to allow the boat to ride up + down WITHOUT bow buoy going under or stern buoy going slack.",
    ],
    pitfalls: [
      "Wrong tide direction at approach — overshoot the bow buoy + need to re-approach.",
      "Too tight on either line — boat doesn't ride the tide + the buoy submerges at HW.",
      "Forgetting to check buoy numbers — picked up the wrong neighbour's mooring.",
      "Lazy line not picked up — at LW the lines may become very slack.",
    ],
  },
  {
    id: "lazy-lines",
    name: "Lazy lines (Mediterranean)",
    summary:
      "Lines pre-laid from a permanent ground tackle to the bow. Each berth has a marker buoy + a lazy line that the marina runs to your bow.",
    whenToUse:
      "Mediterranean marinas where space is at a premium + Med stern-to is the layout. Lazy lines replace dropping your own anchor.",
    linesRequired: [
      "Two stern lines (one each side onto the quay)",
      "Lazy line picked up to the bow + secured to the bow cleat",
    ],
    arrivalSteps: [
      "Brief the crew + check the marina's expected layout (which berth, which lazy line, any windage to anticipate).",
      "Approach the quay in REVERSE — bow into the marina basin so you can see ahead while steering with throttle + rudder.",
      "Marina hand takes your stern lines to the quay cleats + makes them off.",
      "Marina hand passes the lazy-line buoy up to your bow — pull the lazy line up by hand until the slack is taken up + it leads down to the ground tackle.",
      "Tie the lazy line off on the bow cleat.",
      "Take up final tension on stern lines + adjust the lazy line so you sit clear of the quay (gives room to step off).",
      "Rig a gangplank or wide boarding ladder to the stern.",
    ],
    pitfalls: [
      "Crosswind on approach — short Med-style finger berths offer no protection from beam wind. Practise reversing the boat in open water before relying on it.",
      "Picking up the wrong lazy line — neighbour's line crosses + tangles. Always confirm the buoy + line orientation BEFORE pulling.",
      "Going forward on the lazy line under power — line in the propeller. Use the boat-hook + arm-power, not the engine.",
      "Stern line lengths uneven — boat sits off-centre + struggles to balance against the lazy line.",
    ],
  },
  {
    id: "med-stern-to",
    name: "Mediterranean stern-to (own anchor)",
    summary:
      "Same as lazy-line moor but you drop your OWN anchor a boat length or two out from the quay + then back in. Common in smaller harbours without pre-laid lazy lines.",
    whenToUse:
      "Mediterranean harbours where no lazy lines exist; alternative when the marina's lazy lines are damaged or unavailable.",
    linesRequired: [
      "Your own anchor + 30 m+ of chain or rode",
      "Two stern lines for the quay",
    ],
    arrivalSteps: [
      "Approach the quay slowly + identify the target gap; brief crew.",
      "At ~1.5 boat lengths out (further if scope of 5:1 is needed for depth), drop the anchor.",
      "Continue reversing toward the quay, paying out chain or rode steadily as you go.",
      "When you're a few feet off the quay, snub the anchor rode so it brings the bow up.",
      "Marina hand or shore-line crew makes off the stern lines.",
      "Wind in the anchor chain to take up slack + sit the boat clear of the quay.",
      "Set + verify the anchor is holding — apply gentle ASTERN throttle against the chain to test.",
    ],
    pitfalls: [
      "Anchor crossing a neighbour's chain — you can foul each other's anchors on departure. Drop perpendicular to the line of berths, not at an angle.",
      "Anchor dropped too close or too far — close = no scope, far = takes ages to reach the quay + uses all your chain.",
      "Anchor failing to set — the boat is held off by the stern lines but the chain is slack; in a beam wind the boat ranges sideways. Re-set the anchor.",
      "Departure: if a neighbour anchored over your chain, it can take an hour + shouted negotiation to clear.",
    ],
  },
  {
    id: "raft-up",
    name: "Rafting up (alongside another boat already moored)",
    summary:
      "Tie up alongside another yacht already moored, then potentially have a third yacht tie alongside you. Common in busy harbours.",
    whenToUse:
      "When pontoon space is full + the harbour master directs you to raft. Also between friends at a remote anchorage for sociability.",
    linesRequired: [
      "Bow + stern lines to the SHORE side (across or under the inner boat's deck) — your weight is carried by the shore, not by the inner boat",
      "Springs back to the inner boat (NOT to the shore — the inner boat's cleats can take the local loads)",
      "Fenders + a fender board to protect both hulls",
    ],
    arrivalSteps: [
      "ALWAYS ask the inner boat's skipper first — never raft uninvited.",
      "Step the masts staggered (not aligned) — protects rigs in rolling.",
      "Rig fenders + a fender board between hulls.",
      "Bow + stern lines run from your boat over the inner boat's deck to the shore — secured on the shore cleats. This carries your weight without loading the inner boat.",
      "Springs from your boat to the inner boat's cleats — short lines control local fore/aft motion.",
      "Walk across the inner boat's foredeck quietly + only when necessary.",
    ],
    pitfalls: [
      "Loading the inner boat with your lines to the shore — your bow + stern lines should go OVER his deck to shore cleats so HIS boat takes only the spring loads.",
      "Aligned masts — rolling in any swell brings spreaders together; offset by ~1 m.",
      "Walking through the inner boat's cabin — crew may be sleeping. Use the foredeck, quietly.",
      "Leaving the raft early when the inner boat hasn't woken — you'll need them to release a line. Wake them politely.",
    ],
  },
  {
    id: "trot-mooring",
    name: "Trot mooring (rolled trot — common UK)",
    summary:
      "A line of buoys connected by an underwater ground line, each buoy serving one boat. Used in crowded UK rivers + creeks.",
    whenToUse:
      "UK rivers (e.g. Solent, Walton Backwaters, Beaulieu River, parts of the Thames). Issued by the harbour master.",
    linesRequired: ["Bow strop or pendant to the trot buoy", "(Optional) a stern line to the next buoy aft"],
    arrivalSteps: [
      "Confirm the trot number with the harbour master / mooring master.",
      "Approach with the tide on the bow (typical UK river — current dictates orientation).",
      "Pick up the trot buoy with a boat hook and secure the bow strop.",
      "If the trot calls for a stern moor too, drop back on the tide + pick up the aft buoy.",
      "Adjust so the boat lies head-to-tide.",
    ],
    pitfalls: [
      "Picking up the wrong buoy — trots are tightly spaced. Verify number + colour.",
      "Approaching with the tide behind you — overshoot. Always approach into the strongest of wind/tide.",
      "Failing to allow for the slacker tide on a neap — your scope assumptions change.",
    ],
  },
]);

export const MOORING_TECHNIQUE_IDS: readonly MooringTechniqueId[] =
  MOORING_TECHNIQUES.map((t) => t.id);

// ───────────────────────────────────────────────────────────────────
// 2. Anchoring extras — multi-anchor + anchor watch
// ───────────────────────────────────────────────────────────────────

export type AnchorTechniqueId =
  | "single-bower"
  | "bahamian-moor"
  | "fore-and-aft-anchor"
  | "tandem"
  | "kedge-from-stern"
  | "anchor-watch";

export interface AnchorTechnique {
  id: AnchorTechniqueId;
  name: string;
  /// One-line gist.
  summary: string;
  /// When this technique is the right one.
  whenToUse: string;
  /// Step-by-step procedure.
  steps: readonly string[];
  /// Common failure modes + warnings.
  pitfalls: readonly string[];
  /// Number of anchors required + which type each.
  anchorsRequired: string;
}

export const ANCHOR_TECHNIQUES: readonly AnchorTechnique[] = Object.freeze([
  {
    id: "single-bower",
    name: "Single bower anchor (standard)",
    summary:
      "The conventional one-anchor swinging moor — drop bower anchor + lie head-to-wind or head-to-tide.",
    whenToUse:
      "Day stops + overnight in sheltered, predictable conditions with room for the boat to swing 360° on the rode.",
    anchorsRequired: "1 bower anchor + 5:1 (chain) or 7:1 (rode) scope.",
    steps: [
      "Survey the bottom — depth, type, exposure, swinging room around you. Mark a heading on the chartplotter.",
      "Brief crew. Engine + helm covered.",
      "Approach into wind / tide. Stop boat with engine in gentle reverse.",
      "When the boat just starts to gather sternway, drop the anchor + pay out chain steadily as the boat moves backwards.",
      "Pay out 5:1 (depth × 5) for chain, 7:1 for mixed rode.",
      "Snub the chain at the bow stemhead fitting (chain stopper or bowsnubber). The boat now hangs on the chain, not the windlass.",
      "Verify set: light reverse throttle for 30 s + watch a transit ashore — boat shouldn't move.",
      "Plot the anchorage + take a fix every hour to confirm no dragging.",
    ],
    pitfalls: [
      "Anchor dropped on top of a swing — the chain piles up + the anchor never sets properly. ALWAYS pay out as you drift back.",
      "Scope too short — boat drags overnight when tide turns + load comes on differently. ERR generous on scope.",
      "Snubbing on the windlass — winches the anchor + breaks bits of windlass when loads peak.",
      "No transit chosen — can't tell you're dragging until you've already moved.",
    ],
  },
  {
    id: "bahamian-moor",
    name: "Bahamian moor (two anchors, opposite directions)",
    summary:
      "Two anchors set 180° apart so the boat sits at the midpoint between them — small swinging circle, ideal for tidal water with limited space.",
    whenToUse:
      "Tidal anchorages where you don't want to swing on a 360° circle (other boats nearby, narrow channel); strong reversing currents.",
    anchorsRequired:
      "2 anchors — typically the bower forward + a second (often a Danforth or smaller secondary) aft. Both connected to the bow.",
    steps: [
      "Plan the anchorage: identify the direction of the wind / tide reversal + estimate scope.",
      "Drop the FIRST (main) anchor as you would for a single-bower moor, paying out 1.5–2× the scope you'd normally use.",
      "Once the first anchor has set + you've paid out the full extra chain, motor (or drift on the tide) the OPPOSITE direction.",
      "At the far end of the swing — equal distance from the first anchor — drop the SECOND anchor.",
      "Wind in chain on the first anchor + pay out on the second until the boat sits at the midpoint with both anchors taut.",
      "Both rodes lead from the bow — connect both to the bow roller or a bridle.",
      "Verify both anchors are set by lying gently against each in turn.",
    ],
    pitfalls: [
      "Anchors set too close together — the boat doesn't sit at the midpoint + one anchor takes all the load.",
      "Rodes twisting around each other as the tide turns + the boat rotates — over hours, the chains can wrap several times + need untwisting on departure.",
      "Forgotten that the boat is on two anchors when departing — pull only the first one + drag the second under the boat.",
    ],
  },
  {
    id: "fore-and-aft-anchor",
    name: "Fore-and-aft anchor moor",
    summary:
      "Bower anchor off the BOW + a kedge anchor off the STERN — boat held in a fixed orientation, doesn't swing.",
    whenToUse:
      "Tight anchorages with cross-tide; rivers with limited room to swing; alongside a cliff or shore where you need the boat to lie parallel to the shore.",
    anchorsRequired:
      "Bower forward (full size) + kedge aft (lighter, typically a Danforth or Fortress that won't damage the deck if it bounces).",
    steps: [
      "Drop the bower anchor as for a normal moor.",
      "Once set + full scope is out, drop or row out the kedge anchor astern from the dinghy (or motor the boat astern + drop while paying out from the stern).",
      "Adjust both rodes until the boat sits steady in the desired orientation.",
      "Test both anchors are holding before relaxing.",
    ],
    pitfalls: [
      "Kedge anchor lighter — won't hold if wind gets up. Use only in benign + predictable conditions.",
      "Rowing the kedge out from a dinghy in tidal water — current sweeps the dinghy away faster than you can pay out.",
      "Departing: bow first + the kedge can foul the propeller. Recover the kedge BEFORE the bower.",
    ],
  },
  {
    id: "tandem",
    name: "Tandem anchor (two in series on one rode)",
    summary:
      "Two anchors on the same rode — main anchor at the end, secondary anchor a few metres up the chain. Combined holding power for storm-grade weather.",
    whenToUse:
      "Hurricane / TRS-grade weather. When you need maximum holding from one direction + can sacrifice the option of moving easily.",
    anchorsRequired:
      "Two of the largest anchors you carry. Both connected to a single chain at intervals.",
    steps: [
      "Shackle the secondary anchor onto the chain 3-5 m up from the main anchor.",
      "Drop the assembly so the MAIN anchor hits bottom first.",
      "Pay out chain steadily; the second anchor lies in the chain catenary, dragging behind.",
      "Full scope — 7:1 or more.",
      "Both anchors set in tandem; the second adds 50-100% to holding power.",
      "Re-check load through the storm; both anchors share the load.",
    ],
    pitfalls: [
      "Recovery is non-trivial — two anchors on one chain need careful unshackling.",
      "Tandem set requires good bottom + room. Wrong direction sweep = secondary anchor drags + crosses main.",
      "Practise the setup IN PORT before relying on it in a gale.",
    ],
  },
  {
    id: "kedge-from-stern",
    name: "Kedge from stern (manoeuvring + hauling-off)",
    summary:
      "Kedge anchor used from the stern to manoeuvre the boat — kedging off a soft grounding, holding station temporarily, helping the boat onto a wind-favoured heading.",
    whenToUse:
      "Soft grounding in non-tidal mud / sand: kedge out astern + winch the boat off. Also for holding station while you sort an emergency.",
    anchorsRequired: "Kedge anchor + ~30 m of rode (so a lighter Fortress or Danforth).",
    steps: [
      "Row or motor the kedge out astern of the boat in the dinghy.",
      "Drop the kedge + check it bites (a sharp pull confirms set).",
      "Lead the rode back to the boat's stern cleat or capstan / sheet winch.",
      "Take the strain — the boat moves toward the kedge.",
      "Repeat as needed — re-lay the kedge ahead of the boat as it makes progress.",
    ],
    pitfalls: [
      "Wrong direction of pull — kedge doesn't get the boat off the grounding. Survey carefully before laying.",
      "Sheet winch loads — kedging is hard work + winches can fail. Use the windlass if possible.",
      "Dropping kedge over the prop — get it well clear before lowering.",
    ],
  },
  {
    id: "anchor-watch",
    name: "Anchor watch + drag alarms",
    summary:
      "Active monitoring of anchor position — fix, watch transits, GPS anchor-watch alarm, or scheduled crew watch.",
    whenToUse:
      "Overnight in an anchorage, ESPECIALLY when the wind is forecast to shift, build, or rotate; in any anchorage where dragging would put you on a lee shore or among other boats.",
    anchorsRequired: "Just the one main anchor — this is monitoring, not setting another.",
    steps: [
      "On setting: take a bearing or fix from at least two charted features. Note depth, time, anchor symbol.",
      "Set the chartplotter's anchor-watch alarm — usually a circle radius equal to ~1.5× scope at HW.",
      "Set a depth alarm — alerts you if you start dragging into deeper water (or shoaler, if drifting toward the shore).",
      "Crew assigned to watch — typically the helm-on watch — checks position + bearings every hour.",
      "On any wind shift > 30° OR pressure drop > 3 mb / 3 h OR forecast change, take a fresh fix + reset alarms.",
      "If alarm goes — IMMEDIATELY check position by eye + the plotter. If dragging: engage engine, lift anchor, re-lay.",
    ],
    pitfalls: [
      "Anchor alarm radius too small — false alarms every time the boat swings on the tide.",
      "Anchor alarm too large — actually dragging by 50 m before the alarm goes.",
      "Sleeping crew with no watchkeeper — common cause of dragging incidents.",
      "Setting alarms but ignoring them when they go ('false alarm, I'll check in the morning') — by morning, the boat is on a lee shore.",
    ],
  },
]);

export const ANCHOR_TECHNIQUE_IDS: readonly AnchorTechniqueId[] =
  ANCHOR_TECHNIQUES.map((t) => t.id);
